import { round } from 'lodash'

import { Currency } from '../../backend'
import { PlainProduct } from '../hooks/useProducts'

import { ProductOption } from './catalog'

export const getParametersDeltaCents = ({
  product,
  parameters,
  currency,
}: {
  product: PlainProduct
  parameters: Record<string, string | undefined>
  currency: Currency
}) => {
  return (
    (product.parameters ?? [])
      .concat(product.tatParameter ?? [])
      .reduce((prev, { options, id }) => {
        return (
          prev +
          (options.find(option => option.id === parameters[id])
            ?.priceDeltaCents[currency] ?? 0)
        )
      }, 0) ?? 0
  )
}

export const getSamplePrice = ({
  parameters,
  product,
  currency,
}: {
  parameters: Record<string, string | undefined>
  product: PlainProduct
  currency: Currency
}): number | null => {
  return product.fixedUnitPriceCents
    ? round(
        (product.fixedUnitPriceCents[currency] +
          getParametersDeltaCents({
            currency,
            parameters,
            product,
          })) /
          100,
        2,
      )
    : null
}

export const getEstimatedPrice = (
  product: PlainProduct,
  currency: Currency,
): [number, number] | null => {
  if (!product.estimatedPrice) {
    return null
  }
  const minCents = round(product.estimatedPrice.minCents[currency] / 100, 2)
  const maxCents =
    product.estimatedPrice.maxCents &&
    round(product.estimatedPrice.maxCents[currency] / 100, 2)

  return maxCents !== null ? [minCents, maxCents] : [minCents, minCents]
}

export const getPriceRange = (
  product: PlainProduct,
  currency: Currency,
): [number, number] | null => {
  if (product.websitePricingModel === 'estimated') {
    return getEstimatedPrice(product, currency)
  }
  const params = [...(product.parameters ?? [])]
  if (product.tatParameter) {
    params.push(product.tatParameter)
  }

  const [cheapest, mostExpensive] = params
    .reduce(
      (parameterInputValues, { options, id }) => {
        // lookup for most [cheapest, expensive] options for each param
        const [cheap, expensive] = options.reduce(
          (prev, curr) => [
            typeof prev[0] === 'undefined' ||
            prev[0].priceDeltaCents[currency] > curr.priceDeltaCents[currency]
              ? curr
              : prev[0],
            typeof prev[1] === 'undefined' ||
            prev[1].priceDeltaCents[currency] < curr.priceDeltaCents[currency]
              ? curr
              : prev[1],
          ],
          [options[0], options[0]] as [ProductOption, ProductOption],
        )
        return [
          { ...parameterInputValues[0], [id]: cheap.id },
          { ...parameterInputValues[1], [id]: expensive.id },
        ]
      },
      [{}, {}] as Record<string, string>[],
    )
    .map(parameterInputValues =>
      getSamplePrice({ currency, parameters: parameterInputValues, product }),
    )

  if (!cheapest || !mostExpensive) {
    return null
  }

  return [cheapest, mostExpensive]
}

export const serviceFeeCentsPerCurrency: { [c in Currency]: number } = {
  EUR: 9700,
  GBP: 8300,
  USD: 10500,
}
